import Booking from '~/config/Booking'

/**
 * Composable for consistent passenger data management
 * Ensures that all parts of the application use the correct number of passengers
 * from the booking store with proper fallbacks and validation
 */
export const usePassengerData = () => {
  const bookStore = useBookStore()

  /**
   * Get validated passenger data with proper fallbacks
   * @returns {Object} Object containing adults, children, and total passenger count
   */
  const getPassengerData = () => {
    // Get current values from booking store
    const adults = bookStore?.adults ?? Booking.defaultNumberOfAdults
    const children = bookStore?.children ?? Booking.defaultNumberOfChildren

    // Validate and sanitize values
    const validatedAdults = Math.max(1, Math.min(adults, Booking.maxAdultNumber || 8))
    const validatedChildren = Math.max(0, Math.min(children, Booking.maxChildNumber || 6))

    return {
      adults: validatedAdults,
      children: validatedChildren,
      total: validatedAdults + validatedChildren
    }
  }

  /**
   * Get passenger data formatted for API requests
   * @returns {Object} Object with adults and children for API calls
   */
  const getApiPassengerData = () => {
    const { adults, children } = getPassengerData()

    // Log passenger data usage in development for debugging
    if (process.dev) {
      console.log('🧑‍🤝‍🧑 Using passenger data for API request:', { adults, children })
    }

    return { adults, children }
  }

  /**
   * Validate if current passenger configuration is valid
   * @returns {Object} Validation result with isValid flag and error message
   */
  const validatePassengerData = () => {
    const { adults, children, total } = getPassengerData()

    if (total === 0) {
      return {
        isValid: false,
        error: 'At least one passenger is required'
      }
    }

    if (adults === 0) {
      return {
        isValid: false,
        error: 'At least one adult is required'
      }
    }

    if (total > 9) {
      return {
        isValid: false,
        error: 'Maximum 9 passengers allowed'
      }
    }

    return {
      isValid: true,
      error: null
    }
  }

  /**
   * Update passenger counts in the booking store
   * @param {number} adults - Number of adults
   * @param {number} children - Number of children
   */
  const updatePassengerData = (adults, children) => {
    const validatedAdults = Math.max(1, Math.min(adults, Booking.maxAdultNumber || 8))
    const validatedChildren = Math.max(0, Math.min(children, Booking.maxChildNumber || 6))

    bookStore.adults = validatedAdults
    bookStore.children = validatedChildren

    // Update number of tickets to match passenger count
    bookStore.numberOfTickets = validatedAdults + validatedChildren
  }

  /**
   * Reactive computed property for passenger data
   */
  const passengerData = computed(() => getPassengerData())

  /**
   * Get passenger data formatted for hotel room requests
   * @returns {Array} Array of room configurations based on passenger count
   */
  const getRoomConfiguration = () => {
    const { adults, children } = getPassengerData()
    return bookStore.rooms?.length ? bookStore.rooms : [{
      adults: Math.min(adults, 2),
      children: children > 0 ? Array(Math.min(children, 2)).fill(15) : [],
      roomType: adults > 1 ? 'double' : 'single'
    }]
  }

  return {
    getPassengerData,
    getApiPassengerData,
    validatePassengerData,
    updatePassengerData,
    passengerData,
    getRoomConfiguration
  }
}
